import { ZERO } from '@morpho-org/dapp-core'
import { useCallback, useRef } from 'react'

/**
 * Hook to handle paginated offsets
 *
 * @param initialOffset - The initial offset
 * @returns {Object} The hook's return object
 * @returns {React.MutableRefObject<bigint>} paginatedOffset - The current paginated offset value
 * @returns {(page: number) => void} initOffset - Function to initialize the offset for a given page
 * @returns {(value: bigint, page: number) => void} setNextOffset - Function to set the next offset value for a page
 */
export const usePaginatedOffset = (initialOffset?: bigint) => {
  const paginatedOffset = useRef(initialOffset ?? ZERO)
  const offsetsByPage = useRef(new Map<number, bigint>())

  const initOffset = useCallback((page: number) => {
    if (offsetsByPage.current.has(page)) {
      paginatedOffset.current = offsetsByPage.current.get(page)!
    } else if (page === 0) {
      paginatedOffset.current = ZERO
      offsetsByPage.current.clear()
    }
  }, [])

  const setNextOffset = useCallback((value: bigint, page: number) => {
    paginatedOffset.current += value
    offsetsByPage.current.set(page + 1, paginatedOffset.current)
  }, [])

  return {
    paginatedOffset,
    initOffset,
    setNextOffset
  }
}
